import { create } from 'zustand';
import type { Post } from '@shared/types';

export interface PostStats {
  views: number;
  likes: number;
  reposts: number;
  comments: number;
}

export interface FeedState {
  posts: Post[];
  loading: boolean;
  liked: Record<string, true>;
  /** v3.19 自媒体：内容互动统计（本地聚合浏览/赞/转发/评论，展示用）。 */
  stats: Record<string, Partial<PostStats>>;
  setLoading(l: boolean): void;
  setPosts(posts: Post[]): void;
  prepend(post: Post): void;
  upsert(post: Post): void;
  setLiked(id: string, v: boolean): void;
  isLiked(id: string): boolean;
  bumpStat(id: string, key: keyof PostStats, delta?: number): void;
  reset(): void;
}

function merge(list: Post[], incoming: Post[]): Post[] {
  const map = new Map<string, Post>();
  for (const p of list) map.set(p.id, p);
  for (const p of incoming) {
    const prev = map.get(p.id);
    map.set(p.id, prev ? { ...prev, ...p } : p);
  }
  return [...map.values()].sort((a, b) => b.ts - a.ts);
}

export const useFeedStore = create<FeedState>((set, get) => ({
  posts: [],
  loading: false,
  liked: {},
  stats: {},
  setLoading: (l) => set({ loading: l }),
  setPosts: (posts) => set({ posts: merge([], posts) }),
  prepend: (post) => set((s) => ({ posts: merge(s.posts, [post]) })),
  upsert: (post) => set((s) => ({ posts: merge(s.posts, [post]) })),
  setLiked: (id, v) => set((s) => {
    const liked = { ...s.liked };
    if (v) liked[id] = true;
    else delete liked[id];
    return { liked };
  }),
  isLiked: (id) => !!get().liked[id],
  bumpStat: (id, key, delta = 1) => set((s) => {
    const cur = s.stats[id] ?? {};
    const next = { ...cur, [key]: Math.max(0, (cur[key] ?? 0) + delta) };
    return { stats: { ...s.stats, [id]: next } };
  }),
  reset: () => set({ posts: [], loading: false, liked: {}, stats: {} }),
}));

export default useFeedStore;
